import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Heart, Users, Leaf, Target, ArrowRight, 
  Handshake
} from '@phosphor-icons/react';
import GlobalNav from '../../components/GlobalNav';
import { useLanguage } from '../../contexts/LanguageContext';

const About = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const values = [
    {
      icon: Heart,
      title: t('about_value_1_title'),
      description: t('about_value_1_desc')
    },
    {
      icon: Handshake,
      title: t('about_value_2_title'),
      description: t('about_value_2_desc')
    },
    {
      icon: Leaf,
      title: t('about_value_3_title'),
      description: t('about_value_3_desc')
    },
    {
      icon: Users,
      title: t('about_value_4_title'),
      description: t('about_value_4_desc')
    }
  ];

  const milestones = [
    { year: t('about_milestone_1_year'), event: t('about_milestone_1_event'), desc: t('about_milestone_1_desc') },
    { year: t('about_milestone_2_year'), event: t('about_milestone_2_event'), desc: t('about_milestone_2_desc') },
    { year: t('about_milestone_3_year'), event: t('about_milestone_3_event'), desc: t('about_milestone_3_desc') },
    { year: t('about_milestone_4_year'), event: t('about_milestone_4_event'), desc: t('about_milestone_4_desc') }
  ];

  const team = [
    { name: 'Alexandre M.', role: 'Fondateur & CEO', avatar: 'A' },
    { name: 'Sophie L.', role: 'Head of Product', avatar: 'S' },
    { name: 'Thomas D.', role: 'CTO', avatar: 'T' }
  ];

  return (
    <div className="min-h-screen bg-[#0F1A14] text-white">
      <GlobalNav />

      {/* Hero */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-[#C3A365] text-sm tracking-[0.2em] uppercase font-semibold mb-6"
          >
            {t('about_badge')}
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-5xl sm:text-6xl font-light tracking-tight mb-8"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            {t('about_title_1')}<br />
            <span className="text-[#C3A365]">{t('about_title_2')}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-[#8BA89A] max-w-2xl mx-auto leading-relaxed"
          >
            {t('about_desc')}
          </motion.p>
        </div>
      </section>

      {/* Mission */}
      <section className="px-6 py-24 bg-[#18261E]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 bg-[#C3A365]/10 border border-[#C3A365]/30 px-4 py-2 rounded-full mb-6">
                <Target size={16} className="text-[#C3A365]" />
                <span className="text-sm text-[#C3A365] font-medium">{t('about_mission_badge')}</span>
              </div>
              
              <h2 className="text-3xl sm:text-4xl font-light tracking-tight mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {t('about_mission_title')}
              </h2>
              
              <p className="text-[#8BA89A] leading-relaxed mb-6">
                {t('about_mission_p1')}
              </p>
              
              <p className="text-[#8BA89A] leading-relaxed">
                {t('about_mission_p2')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              {[
                { value: '150+', label: t('about_stat_merchants') },
                { value: '25K+', label: t('about_stat_members') },
                { value: '500K+', label: t('about_stat_scans') },
                { value: '98%', label: t('about_stat_satisfaction') }
              ].map((stat, i) => (
                <div 
                  key={i} 
                  className="p-6 bg-[#1E2F26] border border-[#283D32] text-center"
                >
                  <p className="text-3xl font-light text-[#C3A365] mb-1">{stat.value}</p>
                  <p className="text-[#8BA89A] text-sm">{stat.label}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="px-6 py-24 bg-[#0F1A14]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#C3A365] text-sm tracking-[0.2em] uppercase font-semibold mb-4">
              {t('about_values_badge')}
            </p>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t('about_values_title')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, i) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className="p-6 bg-[#1E2F26]/50 border border-[#283D32] hover:border-[#C3A365]/30 transition-all"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#C3A365]/10 flex items-center justify-center mb-4">
                    <Icon size={24} className="text-[#C3A365]" weight="duotone" />
                  </div>
                  <h3 className="text-lg font-medium text-white mb-2">{value.title}</h3>
                  <p className="text-[#8BA89A] text-sm leading-relaxed">{value.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="px-6 py-24 bg-[#18261E]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#C3A365] text-sm tracking-[0.2em] uppercase font-semibold mb-4">
              {t('about_timeline_badge')}
            </p>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t('about_timeline_title')}
            </h2>
          </div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-8 top-0 bottom-0 w-px bg-gradient-to-b from-[#C3A365] to-transparent hidden sm:block" />

            <div className="space-y-8">
              {milestones.map((milestone, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className="flex gap-6"
                >
                  <div className="hidden sm:flex flex-col items-center">
                    <div className="w-16 h-16 rounded-full bg-[#C3A365]/10 border border-[#C3A365]/30 flex items-center justify-center">
                      <span className="text-[#C3A365] font-bold">{milestone.year}</span>
                    </div>
                  </div>
                  <div className="flex-1 p-6 bg-[#1E2F26] border border-[#283D32]">
                    <span className="text-[#C3A365] text-sm font-bold sm:hidden">{milestone.year}</span>
                    <h3 className="text-lg font-medium text-white mb-1">{milestone.event}</h3>
                    <p className="text-[#8BA89A] text-sm">{milestone.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="px-6 py-24 bg-[#0F1A14]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#C3A365] text-sm tracking-[0.2em] uppercase font-semibold mb-4">
              {t('about_team_badge')}
            </p>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t('about_team_title')}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {team.map((member, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="text-center p-8 bg-[#1E2F26]/50 border border-[#283D32]"
              >
                <div className="w-20 h-20 rounded-full bg-[#C3A365] flex items-center justify-center text-[#0F1A14] text-2xl font-bold mx-auto mb-4">
                  {member.avatar}
                </div>
                <h3 className="text-lg font-medium text-white mb-1">{member.name}</h3>
                <p className="text-[#C3A365] text-sm">{member.role}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 py-24 bg-gradient-to-b from-[#18261E] to-[#0F1A14]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-light tracking-tight mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {t('about_cta_title')}
          </h2>
          <p className="text-[#8BA89A] mb-8">
            {t('about_cta_desc')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <motion.button
              onClick={() => navigate('/activer')}
              className="px-8 py-4 bg-[#C3A365] text-[#0F1A14] font-semibold hover:bg-[#D4B576] transition-colors flex items-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="about-cta-member"
            >
              {t('about_cta_member')} <ArrowRight size={18} />
            </motion.button>
            <motion.button
              onClick={() => navigate('/contact')}
              className="px-8 py-4 border border-[#3E5248] text-white font-medium hover:border-[#C3A365]/50 transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="about-cta-contact"
            >
              {t('about_cta_contact')}
            </motion.button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-[#1E2F26]">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <span className="brand-logo text-xl text-[#8BA89A]">habitué</span>
          <p className="text-xs text-[#8BA89A]">© 2025 habitué. {t('footer_rights')}</p>
        </div>
      </footer>
    </div>
  );
};

export default About;
